import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  ChartOptions,
  ChartData,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import { calculatePercentile } from "utils/math";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface DayBarGraphProps {
  values: number[];
  startDate: Date;
  getTooltipLabel?: (index: number) => string;
}

const formatDate = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const generateLabels = (data: number[], startDate: Date): string[] => {
  const labels = data.map((_, index) => {
    if (index === 0) {
      return formatDate(startDate);
    } else if (index % 7 === 0) {
      const labelDate = new Date(startDate);
      labelDate.setDate(startDate.getDate() + index);
      return formatDate(labelDate);
    } else {
      return ""; // no label
    }
  });
  return labels;
};

const DayBarGraph = ({
  values,
  startDate,
  getTooltipLabel,
}: DayBarGraphProps) => {
  const pctg25 = calculatePercentile(values, 25);
  const pctg75 = calculatePercentile(values, 75);

  const getColor = (value: number): string => {
    if (value >= pctg75) return "rgba(95, 67, 178, 0.8)";
    else if (value >= pctg25) return "rgba(163, 132, 255, 0.8)";
    else return "rgba(201, 165, 255, 0.8)";
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        max: Math.round(Math.max(...values) / 0.8),
      },
    },
    plugins: {
      tooltip: {
        enabled: getTooltipLabel !== undefined,
        intersect: false,
        callbacks: {
          ...(getTooltipLabel && {
            label: (tooltipItem) => getTooltipLabel(tooltipItem.dataIndex),
          }),
          title: (tooltipItems) => {
            const newDate = new Date(startDate);
            newDate.setDate(startDate.getDate() + tooltipItems[0].dataIndex);
            return formatDate(newDate);
          },
        },
      },
      legend: {
        display: false,
      },
    },
  };

  const data: ChartData<"bar", number[], string> = {
    labels: generateLabels(values, startDate),
    datasets: [
      {
        label: "Dataset",
        data: values,
        backgroundColor: values.map((value) => getColor(value)),
      },
    ],
  };

  return <Bar options={options} data={data} />;
};

export default DayBarGraph;
